import { type FC, useEffect, useState } from "react";

import { Plus } from "lucide-react";
import { enqueueSnackbar } from "notistack";

import { Button } from "@/shared/components/ui/button";
import { useEstateItems } from "@/shared/hooks/useEstateItems";
import { filterByCategory } from "@/shared/constants/estateCategories";

import { parseAddress } from "@/shared/utils/addressUtils";

import { LegalAuthorityPropertyForm } from "./LegalAuthorityAddPropertyForm";
import { LegalAuthorityPropertyCard } from "./LegalAuthorityPropertyCard";
import { LegalAuthorityStepLayout } from "./LegalAuthorityStepLayout";

import type { AddPropertyFormValues } from "./LegalAuthorityAddPropertyForm";
import type { EstateItem } from "@/shared/types/estateTypes";

function itemToFormValues(item: EstateItem, ownership: string): AddPropertyFormValues {
  const meta = item.metadata as Record<string, string> | undefined;
  const hasAddressMeta = meta?.street_address && meta?.city;
  const parsed = hasAddressMeta ? null : parseAddress(item.item_name);
  return {
    address: {
      streetAddress: meta?.street_address ?? parsed?.streetAddress ?? item.item_name,
      apartment: meta?.apartment ?? parsed?.apartment ?? "",
      city: meta?.city ?? parsed?.city ?? "",
      state: meta?.state ?? parsed?.state ?? "",
      zipCode: meta?.zip_code ?? parsed?.zipCode ?? "",
    },
    propertyType: item.item_type ?? "",
    estimatedValue: item.balance ? String(item.balance) : "",
    ownership,
  };
}

interface LegalAuthorityPropertyStepProps {
  personFirstName: string;
  estateId: number;
  willDocumentId?: number;
  savedOwnerships?: Record<number, string> | null;
  portalContainer?: HTMLElement | null;
  onBack: () => void;
  onContinue: (propertyOwnerships: Record<number, string>) => void;
}

export const LegalAuthorityPropertyStep: FC<LegalAuthorityPropertyStepProps> = ({
  personFirstName,
  estateId,
  willDocumentId,
  savedOwnerships,
  portalContainer,
  onBack,
  onContinue,
}) => {
  const { assets, isLoading, addItem, updateItem, deleteItem } = useEstateItems(estateId);
  const properties = filterByCategory(assets, "Properties");
  const [ownershipMap, setOwnershipMap] = useState<Record<number, string>>(() => {
    if (!savedOwnerships) return {};
    const normalized: Record<number, string> = {};
    for (const [key, value] of Object.entries(savedOwnerships)) {
      normalized[Number(key)] = value;
    }
    return normalized;
  });
  const [isAdding, setIsAdding] = useState(false);
  const [editingPropertyId, setEditingPropertyId] = useState<number | null>(null);
  const [missingOwnership, setMissingOwnership] = useState<Set<number>>(new Set());
  const [removingProperty, setRemovingProperty] = useState<{ id: number; name: string } | null>(
    null,
  );
  const [manuallyAdded, setManuallyAdded] = useState<Set<number>>(new Set());
  const [pendingOwnership, setPendingOwnership] = useState<{
    itemName: string;
    ownership: string;
  } | null>(null);

  useEffect(() => {
    if (!savedOwnerships) return;
    setOwnershipMap((prev) => {
      const hasValues = Object.keys(prev).length > 0;
      if (hasValues) return prev;
      const normalized: Record<number, string> = {};
      for (const [key, value] of Object.entries(savedOwnerships)) {
        normalized[Number(key)] = value;
      }
      return normalized;
    });
  }, [savedOwnerships]);

  useEffect(() => {
    if (!pendingOwnership) return;
    const match = properties.find((p) => p.item_name === pendingOwnership.itemName);
    if (match) {
      setOwnershipMap((prev) => ({ ...prev, [match.id]: pendingOwnership.ownership }));
      setManuallyAdded((prev) => new Set(prev).add(match.id));
      setPendingOwnership(null);
    }
  }, [properties, pendingOwnership]);

  const personName = personFirstName || "your loved one";
  const isEditing = isAdding || editingPropertyId !== null;
  const isEmpty = properties.length === 0 && !isAdding;

  const handleConfirm = async () => {
    if (isEditing) return;
    if (properties.length > 0) {
      const missing = new Set(
        properties.filter((p) => !manuallyAdded.has(p.id) && !ownershipMap[p.id]).map((p) => p.id),
      );
      if (missing.size > 0) {
        setMissingOwnership(missing);
        return;
      }
      const toUpdate = properties.filter((p) => {
        if (manuallyAdded.has(p.id)) return false;
        return !p.source_document_id || p.extraction_status === "pending";
      });
      if (toUpdate.length > 0) {
        const updates = toUpdate.map((p) =>
          updateItem(p.id, {
            ...(willDocumentId && !p.source_document_id
              ? { source_document_id: willDocumentId }
              : {}),
            extraction_status: "approved",
          }),
        );
        await Promise.all(updates).catch(console.error);
      }
    }
    onContinue(ownershipMap);
  };

  const buildItemFields = (form: AddPropertyFormValues) => {
    const { address } = form;
    const parts = [address.streetAddress, address.apartment].filter(Boolean);
    return {
      item_name: `${parts.join(" ")}, ${address.city}, ${address.state} ${address.zipCode}`,
      item_type: form.propertyType || undefined,
      balance: form.estimatedValue ? parseFloat(form.estimatedValue.replace(/[^0-9.]/g, "")) : 0,
      is_estimated: true,
      metadata: {
        street_address: address.streetAddress,
        apartment: address.apartment || undefined,
        city: address.city,
        state: address.state,
        zip_code: address.zipCode,
      },
    };
  };

  const handleAdd = async (form: AddPropertyFormValues) => {
    const fields = buildItemFields(form);
    const result = await addItem({
      estate_id: estateId,
      category: "Properties",
      classification: "asset",
      source_document_id: willDocumentId ?? null,
      ...fields,
    });
    const newId = (result as { id?: number } | undefined)?.id;
    if (newId && form.ownership) {
      setOwnershipMap((prev) => ({ ...prev, [newId]: form.ownership }));
      setManuallyAdded((prev) => new Set(prev).add(newId));
    } else if (form.ownership) {
      setPendingOwnership({ itemName: fields.item_name, ownership: form.ownership });
    }
    setIsAdding(false);
  };

  const handleEdit = async (form: AddPropertyFormValues) => {
    if (editingPropertyId === null) return;
    await updateItem(editingPropertyId, buildItemFields(form));
    if (form.ownership) {
      setOwnershipMap((prev) => ({ ...prev, [editingPropertyId]: form.ownership }));
    }
    setEditingPropertyId(null);
  };

  const handleRemove = (id: number, name: string) => {
    setRemovingProperty({ id, name });
  };

  const confirmRemove = () => {
    if (!removingProperty) return;
    const { id } = removingProperty;
    setRemovingProperty(null);
    setEditingPropertyId(null);
    setOwnershipMap((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    deleteItem(id).catch((error) => {
      console.error(error);
      enqueueSnackbar("Couldn't remove this property. Please try again.", { variant: "error" });
    });
  };

  if (isLoading) {
    return (
      <LegalAuthorityStepLayout
        title={`Property found in ${personName}'s name`}
        description="Check that these details are right. Property is often the largest part of an estate, so it deserves a careful look."
        activeStep={3}
        onBack={onBack}
      >
        <div className="flex items-center justify-center py-12">
          <p className="text-sm text-secondary">Loading properties...</p>
        </div>
      </LegalAuthorityStepLayout>
    );
  }

  return (
    <>
      <LegalAuthorityStepLayout
        title={
          isEmpty
            ? `We didn't find property in ${personName}'s name`
            : `Property found in ${personName}'s name`
        }
        description={
          isEmpty
            ? undefined
            : "Check that these details are right. Property is often the largest part of an estate, so it deserves a careful look."
        }
        activeStep={3}
        onBack={isEditing ? undefined : onBack}
        footerLeft={
          isEditing ? (
            <p className="text-sm text-secondary">Finish editing to confirm</p>
          ) : undefined
        }
        actionButton={
          isEmpty ? (
            <Button variant="outline" size="md" onClick={handleConfirm}>
              {personName} didn't own property
            </Button>
          ) : (
            <Button variant="brand" size="md" disabled={isEditing} onClick={handleConfirm}>
              Confirm and continue
            </Button>
          )
        }
      >
        {properties.map((property) =>
          editingPropertyId === property.id ? (
            <LegalAuthorityPropertyForm
              key={property.id}
              mode="edit"
              personFirstName={personName}
              estateId={estateId}
              portalContainer={portalContainer}
              initialValues={itemToFormValues(property, ownershipMap[property.id] ?? "")}
              onSubmit={handleEdit}
              onCancel={() => setEditingPropertyId(null)}
              onRemove={() => handleRemove(property.id, property.item_name)}
            />
          ) : (
            <div key={property.id} className={isEditing ? "opacity-70" : undefined}>
              <LegalAuthorityPropertyCard
                property={property}
                personFirstName={personName}
                selectedOwnership={ownershipMap[property.id] ?? null}
                hideOwnership={manuallyAdded.has(property.id) || !!savedOwnerships?.[property.id]}
                hideActions={isEditing}
                showOwnershipError={missingOwnership.has(property.id)}
                onOwnershipChange={(value) => {
                  setOwnershipMap((prev) => ({ ...prev, [property.id]: value }));
                  setMissingOwnership((prev) => {
                    const next = new Set(prev);
                    next.delete(property.id);
                    return next;
                  });
                }}
                onEdit={() => setEditingPropertyId(property.id)}
                onRemove={() => handleRemove(property.id, property.item_name)}
              />
            </div>
          ),
        )}

        {isAdding ? (
          <LegalAuthorityPropertyForm
            mode="add"
            personFirstName={personName}
            estateId={estateId}
            portalContainer={portalContainer}
            onSubmit={handleAdd}
            onCancel={() => setIsAdding(false)}
          />
        ) : isEmpty ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 rounded-lg border border-dashed border-primary bg-white p-6 text-center sm:p-10">
            <div className="flex flex-col gap-2">
              <p className="text-base font-semibold tracking-[-0.32px]">
                Know of a home or land {personName} owned?
              </p>
              <p className="text-sm text-secondary">
                If you know of a home or land {personName} owned, add it below.
              </p>
            </div>
            <Button variant="brand" size="md" onClick={() => setIsAdding(true)}>
              <Plus className="h-4 w-4" />
              Add a property
            </Button>
          </div>
        ) : !isEditing ? (
          <Button
            variant="outline"
            className="w-full rounded-full"
            onClick={() => setIsAdding(true)}
          >
            <Plus className="h-4 w-4" />
            Add another property
          </Button>
        ) : null}
      </LegalAuthorityStepLayout>

      {removingProperty && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/30"
            onClick={() => setRemovingProperty(null)}
            role="presentation"
          />
          <div className="relative z-10 flex w-full max-w-[448px] flex-col gap-6 rounded-lg border border-primary bg-white p-6 shadow-lg">
            <div className="flex flex-col gap-2">
              <p className="text-base font-semibold">Remove this property?</p>
              <p className="text-base text-secondary">
                {removingProperty.name} will be removed from {personName}'s estate. Change your mind
                later? Add it back anytime
              </p>
            </div>
            <div className="flex gap-4">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setRemovingProperty(null)}
              >
                Cancel
              </Button>
              <Button variant="destructive" className="flex-1" onClick={confirmRemove}>
                Remove
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
